/**
 * PSH Seasonal Worlds — Centralized Design System & Theme Engine
 * 
 * Supports 9 seasonal event presets:
 * - default (Cyber Core fallback)
 * - halloween (Haunted Cyberpunk)
 * - christmas (Winter Wonderland)
 * - newyear (Midnight Celebration)
 * - valentines (Romantic Neon)
 * - blackfriday (High-Impact Sale)
 * - diwali (Festival of Lights)
 * - eid (Elegant Celebration)
 * - summersale (Tropical Energy)
 * 
 * Design Tokens include:
 * - Tailwind RGB color channels (--primary-50 to --primary-900, --secondary-*, --accent-*)
 * - Semantic theme tokens (--color-primary, --color-surface, --color-border, --color-glow, etc.)
 * - Rich campaign copy, CTAs, and decorative artwork motifs
 * - Date-based campaign scheduling & URL preview support
 */

export const themes = {
    "default": {
        "id": "default",
        "name": "Default",
        "title": "Cyber Core",
        "eventName": "PSH Store",
        "effect": "none",
        "schedule": null,
        "backgroundPattern": "radial-gradient(circle at 50% 50%, rgba(59, 130, 246, 0.04) 0%, transparent 60%)",
        "colors": {
            "--primary-50": "239 246 255",
            "--primary-100": "219 234 254",
            "--primary-200": "191 219 254",
            "--primary-300": "147 197 253",
            "--primary-400": "96 165 250",
            "--primary-500": "59 130 246",
            "--primary-600": "37 99 235",
            "--primary-700": "29 78 216",
            "--primary-800": "30 64 175",
            "--primary-900": "30 58 138",
            "--secondary-50": "240 249 255",
            "--secondary-100": "224 242 254",
            "--secondary-200": "186 230 253",
            "--secondary-300": "125 211 252",
            "--secondary-400": "56 189 248",
            "--secondary-500": "14 165 233",
            "--secondary-600": "2 132 199",
            "--secondary-700": "3 105 161",
            "--secondary-800": "7 89 133",
            "--secondary-900": "12 74 110",
            "--accent-50": "254 247 255",
            "--accent-100": "252 238 255",
            "--accent-200": "248 218 255",
            "--accent-300": "242 184 255",
            "--accent-400": "232 121 255",
            "--accent-500": "217 70 239",
            "--accent-600": "192 38 211",
            "--accent-700": "162 28 175",
            "--accent-800": "134 25 143",
            "--accent-900": "112 26 117"
        },
        "tokens": {
            "--color-primary": "#3B82F6",
            "--color-secondary": "#0EA5E9",
            "--color-accent": "#D946EF",
            "--color-crimson": "#EF4444",
            "--color-background": "#0F172A",
            "--color-surface": "rgba(30, 41, 59, 0.72)",
            "--color-surface-elevated": "rgba(51, 65, 85, 0.85)",
            "--color-text-primary": "#F8FAFC",
            "--color-text-secondary": "#94A3B8",
            "--color-border": "rgba(59, 130, 246, 0.25)",
            "--color-glow": "rgba(59, 130, 246, 0.35)",
            "--gradient-hero": "linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)",
            "--gradient-promo": "linear-gradient(90deg, #3B82F6 0%, #0EA5E9 50%, #D946EF 100%)",
            "--shadow-card": "0 8px 32px -4px rgba(59, 130, 246, 0.12)",
            "--radius-card": "1.25rem"
        },
        "campaign": {
            "badge": "\u26a1 PSH Store Elite Tools",
            "headline": "Next-Gen Game Utilities & Mod Scripts",
            "description": "Verified GameGuardian scripts, RuntimeX tools, and VIP gaming utilities with instant activation.",
            "ctaPrimary": {
                "label": "Explore All Tools",
                "action": "/products"
            },
            "ctaSecondary": {
                "label": "Special Bundles",
                "action": "/products?category=bundle"
            },
            "motif": "cyber-grid",
            "secondarySlide": {
                "badge": "\ud83d\ude80 Community Network",
                "headline": "Connect Telegram for Instant Alerts",
                "description": "Get real-time order status updates, private group access, and direct customer support.",
                "ctaPrimary": {
                    "label": "Join Community",
                    "action": "https://t.me/pshstore"
                },
                "ctaSecondary": {
                    "label": "Bind Telegram Account",
                    "action": "/profile"
                }
            }
        }
    },
    "halloween": {
        "id": "halloween",
        "name": "Halloween",
        "title": "Haunted Cyberpunk",
        "eventName": "Halloween",
        "effect": "spooky",
        "schedule": {
            "start": "10-15",
            "end": "11-05"
        },
        "backgroundPattern": "radial-gradient(circle at 85% 15%, rgba(255, 106, 0, 0.18) 0%, rgba(217, 46, 72, 0.12) 25%, transparent 55%), radial-gradient(circle at 15% 80%, rgba(139, 69, 255, 0.18) 0%, rgba(36, 16, 47, 0.25) 45%, transparent 65%), radial-gradient(circle at 50% 0%, rgba(255, 106, 0, 0.10) 0%, rgba(16, 8, 23, 0.12) 70%, transparent 100%)",
        "colors": {
            "--primary-50": "255 247 237",
            "--primary-100": "255 237 213",
            "--primary-200": "254 215 170",
            "--primary-300": "253 186 116",
            "--primary-400": "251 146 60",
            "--primary-500": "255 106 0",
            "--primary-600": "234 88 12",
            "--primary-700": "194 65 12",
            "--primary-800": "154 52 18",
            "--primary-900": "124 45 18",
            "--secondary-50": "250 245 255",
            "--secondary-100": "243 232 255",
            "--secondary-200": "233 213 255",
            "--secondary-300": "216 180 254",
            "--secondary-400": "192 132 252",
            "--secondary-500": "139 69 255",
            "--secondary-600": "126 34 206",
            "--secondary-700": "107 33 168",
            "--secondary-800": "88 28 135",
            "--secondary-900": "59 7 100",
            "--accent-50": "240 253 255",
            "--accent-100": "224 249 254",
            "--accent-200": "186 242 253",
            "--accent-300": "125 231 251",
            "--accent-400": "56 217 249",
            "--accent-500": "32 201 245",
            "--accent-600": "8 145 178",
            "--accent-700": "14 116 144",
            "--accent-800": "21 94 117",
            "--accent-900": "22 78 99"
        },
        "tokens": {
            "--color-primary": "#FF6A00",
            "--color-secondary": "#8B45FF",
            "--color-accent": "#20C9F5",
            "--color-crimson": "#D92E48",
            "--color-background": "#100817",
            "--color-surface": "rgba(36, 16, 47, 0.72)",
            "--color-surface-elevated": "rgba(45, 20, 60, 0.85)",
            "--color-text-primary": "#F5EBDD",
            "--color-text-secondary": "#A89BB5",
            "--color-border": "rgba(255, 106, 0, 0.25)",
            "--color-glow": "rgba(255, 106, 0, 0.45)",
            "--gradient-hero": "linear-gradient(135deg, rgba(36, 16, 47, 0.95) 0%, rgba(16, 8, 23, 0.98) 100%)",
            "--gradient-promo": "linear-gradient(90deg, #FF6A00 0%, #D92E48 50%, #8B45FF 100%)",
            "--shadow-card": "0 8px 32px -4px rgba(255, 106, 0, 0.12), 0 4px 16px -2px rgba(139, 69, 255, 0.15)",
            "--radius-card": "1.25rem"
        },
        "campaign": {
            "badge": "\ud83c\udf83 Haunted Harvest 2026",
            "headline": "Enter the Haunted Store",
            "description": "Discover spooky deals, exclusive bundles, and limited-time drops",
            "ctaPrimary": {
                "label": "Explore Halloween Deals",
                "action": "/products"
            },
            "ctaSecondary": {
                "label": "Claim Mystery Loot",
                "action": "/products?category=bundle"
            },
            "motif": "jack-o-lantern",
            "secondarySlide": {
                "badge": "\ud83e\udd87 Telegram Infiltration",
                "headline": "Exclusive Drops & Zero-Day Scripts",
                "description": "Join the inner crypt on Telegram for private scripts, instant order alerts, and secret flash discount codes.",
                "ctaPrimary": {
                    "label": "Join Telegram",
                    "action": "https://t.me/pshstore"
                },
                "ctaSecondary": {
                    "label": "Bind Profile",
                    "action": "/profile"
                }
            }
        }
    },
    "christmas": {
        "id": "christmas",
        "name": "Christmas",
        "title": "Winter Wonderland",
        "eventName": "Christmas",
        "effect": "snow",
        "schedule": {
            "start": "12-01",
            "end": "12-28"
        },
        "backgroundPattern": "radial-gradient(circle at 15% 15%, rgba(239, 68, 68, 0.12) 0%, transparent 45%), radial-gradient(circle at 85% 85%, rgba(16, 185, 129, 0.12) 0%, transparent 50%), radial-gradient(circle at 50% 0%, rgba(251, 191, 36, 0.08) 0%, transparent 55%)",
        "colors": {
            "--primary-50": "254 242 242",
            "--primary-100": "254 226 226",
            "--primary-200": "254 202 202",
            "--primary-300": "252 165 165",
            "--primary-400": "248 113 113",
            "--primary-500": "239 68 68",
            "--primary-600": "220 38 38",
            "--primary-700": "185 28 28",
            "--primary-800": "153 27 27",
            "--primary-900": "127 29 29",
            "--secondary-50": "240 253 244",
            "--secondary-100": "220 252 231",
            "--secondary-200": "187 247 208",
            "--secondary-300": "134 239 172",
            "--secondary-400": "74 222 128",
            "--secondary-500": "34 197 94",
            "--secondary-600": "22 163 74",
            "--secondary-700": "21 128 61",
            "--secondary-800": "22 101 52",
            "--secondary-900": "20 83 45",
            "--accent-50": "255 251 235",
            "--accent-100": "254 243 199",
            "--accent-200": "253 230 138",
            "--accent-300": "252 211 77",
            "--accent-400": "251 191 36",
            "--accent-500": "245 158 11",
            "--accent-600": "217 119 6",
            "--accent-700": "180 83 9",
            "--accent-800": "146 64 14",
            "--accent-900": "120 53 15"
        },
        "tokens": {
            "--color-primary": "#EF4444",
            "--color-secondary": "#10B981",
            "--color-accent": "#FBBF24",
            "--color-crimson": "#DC2626",
            "--color-background": "#0B132B",
            "--color-surface": "rgba(18, 30, 60, 0.72)",
            "--color-surface-elevated": "rgba(28, 45, 85, 0.85)",
            "--color-text-primary": "#F8FAFC",
            "--color-text-secondary": "#94A3B8",
            "--color-border": "rgba(239, 68, 68, 0.25)",
            "--color-glow": "rgba(239, 68, 68, 0.35)",
            "--gradient-hero": "linear-gradient(135deg, rgba(18, 30, 60, 0.95) 0%, rgba(11, 19, 43, 0.98) 100%)",
            "--gradient-promo": "linear-gradient(90deg, #EF4444 0%, #F59E0B 50%, #10B981 100%)",
            "--shadow-card": "0 8px 32px -4px rgba(239, 68, 68, 0.12), 0 4px 16px -2px rgba(16, 185, 129, 0.12)",
            "--radius-card": "1.25rem"
        },
        "campaign": {
            "badge": "\ud83c\udf84 Winter Holiday Fest",
            "headline": "Unwrap Frosty Savings",
            "description": "Celebrate the season of giving with enchanted mod scripts and festive discounts",
            "ctaPrimary": {
                "label": "Explore Holiday Gifts",
                "action": "/products"
            },
            "ctaSecondary": {
                "label": "Winter Bundles",
                "action": "/products?category=bundle"
            },
            "motif": "pine-tree",
            "secondarySlide": {
                "badge": "\ud83c\udf85 North Pole Express",
                "headline": "Gift Alerts & Instant Delivery",
                "description": "Link your Telegram to get real-time delivery notices and holiday exclusive freebies right to your inbox.",
                "ctaPrimary": {
                    "label": "Connect Telegram",
                    "action": "https://t.me/pshstore"
                },
                "ctaSecondary": {
                    "label": "Account Settings",
                    "action": "/profile"
                }
            }
        }
    },
    "newyear": {
        "id": "newyear",
        "name": "New Year",
        "title": "Midnight Celebration",
        "eventName": "New Year",
        "effect": "celebration",
        "schedule": {
            "start": "12-29",
            "end": "01-10"
        },
        "backgroundPattern": "radial-gradient(circle at 80% 20%, rgba(245, 158, 11, 0.15) 0%, transparent 45%), radial-gradient(circle at 20% 80%, rgba(99, 102, 241, 0.15) 0%, transparent 50%), radial-gradient(circle at 50% 50%, rgba(236, 72, 153, 0.08) 0%, transparent 60%)",
        "colors": {
            "--primary-50": "255 251 235",
            "--primary-100": "254 243 199",
            "--primary-200": "253 230 138",
            "--primary-300": "252 211 77",
            "--primary-400": "251 191 36",
            "--primary-500": "245 158 11",
            "--primary-600": "217 119 6",
            "--primary-700": "180 83 9",
            "--primary-800": "146 64 14",
            "--primary-900": "120 53 15",
            "--secondary-50": "238 242 255",
            "--secondary-100": "224 231 255",
            "--secondary-200": "199 210 254",
            "--secondary-300": "165 180 252",
            "--secondary-400": "129 140 248",
            "--secondary-500": "99 102 241",
            "--secondary-600": "79 70 229",
            "--secondary-700": "67 56 202",
            "--secondary-800": "55 48 163",
            "--secondary-900": "49 46 129",
            "--accent-50": "253 242 248",
            "--accent-100": "252 231 243",
            "--accent-200": "249 168 212",
            "--accent-300": "244 114 182",
            "--accent-400": "236 72 153",
            "--accent-500": "219 39 119",
            "--accent-600": "190 24 93",
            "--accent-700": "157 23 77",
            "--accent-800": "131 24 67",
            "--accent-900": "112 26 63"
        },
        "tokens": {
            "--color-primary": "#F59E0B",
            "--color-secondary": "#6366F1",
            "--color-accent": "#EC4899",
            "--color-crimson": "#E11D48",
            "--color-background": "#090A1A",
            "--color-surface": "rgba(20, 24, 52, 0.72)",
            "--color-surface-elevated": "rgba(32, 38, 78, 0.85)",
            "--color-text-primary": "#FEF3C7",
            "--color-text-secondary": "#A5B4FC",
            "--color-border": "rgba(245, 158, 11, 0.25)",
            "--color-glow": "rgba(245, 158, 11, 0.4)",
            "--gradient-hero": "linear-gradient(135deg, rgba(20, 24, 52, 0.95) 0%, rgba(9, 10, 26, 0.98) 100%)",
            "--gradient-promo": "linear-gradient(90deg, #F59E0B 0%, #EC4899 50%, #6366F1 100%)",
            "--shadow-card": "0 8px 32px -4px rgba(245, 158, 11, 0.15)",
            "--radius-card": "1.25rem"
        },
        "campaign": {
            "badge": "\ud83c\udf86 New Year 2026 Blast",
            "headline": "Level Up for the New Year",
            "description": "Ring in the new year with premier gaming utilities, explosive deals, and VIP upgrades",
            "ctaPrimary": {
                "label": "Shop New Year Drops",
                "action": "/products"
            },
            "ctaSecondary": {
                "label": "VIP Memberships",
                "action": "/products?category=subscription"
            },
            "motif": "firework",
            "secondarySlide": {
                "badge": "\ud83e\udd42 Countdown Alerts",
                "headline": "Direct Flash Sale Warnings",
                "description": "Catch midnight drops the instant they happen on Telegram before stock runs out.",
                "ctaPrimary": {
                    "label": "Join Alert Channel",
                    "action": "https://t.me/pshstore"
                },
                "ctaSecondary": {
                    "label": "Link Account",
                    "action": "/profile"
                }
            }
        }
    },
    "valentines": {
        "id": "valentines",
        "name": "Valentines",
        "title": "Romantic Neon",
        "eventName": "Valentines",
        "effect": "hearts",
        "schedule": {
            "start": "02-07",
            "end": "02-21"
        },
        "backgroundPattern": "radial-gradient(circle at 80% 15%, rgba(244, 63, 94, 0.16) 0%, transparent 45%), radial-gradient(circle at 20% 85%, rgba(225, 29, 72, 0.14) 0%, transparent 50%), radial-gradient(circle at 50% 50%, rgba(251, 113, 133, 0.08) 0%, transparent 65%)",
        "colors": {
            "--primary-50": "255 241 242",
            "--primary-100": "255 228 230",
            "--primary-200": "254 205 211",
            "--primary-300": "253 164 175",
            "--primary-400": "251 113 133",
            "--primary-500": "244 63 94",
            "--primary-600": "225 29 72",
            "--primary-700": "190 18 60",
            "--primary-800": "159 18 57",
            "--primary-900": "136 19 55",
            "--secondary-50": "254 242 242",
            "--secondary-100": "254 226 226",
            "--secondary-200": "254 202 202",
            "--secondary-300": "252 165 165",
            "--secondary-400": "248 113 113",
            "--secondary-500": "239 68 68",
            "--secondary-600": "220 38 38",
            "--secondary-700": "185 28 28",
            "--secondary-800": "153 27 27",
            "--secondary-900": "127 29 29",
            "--accent-50": "255 241 242",
            "--accent-100": "255 228 230",
            "--accent-200": "254 205 211",
            "--accent-300": "253 164 175",
            "--accent-400": "251 113 133",
            "--accent-500": "244 63 94",
            "--accent-600": "225 29 72",
            "--accent-700": "190 18 60",
            "--accent-800": "159 18 57",
            "--accent-900": "136 19 55"
        },
        "tokens": {
            "--color-primary": "#E11D48",
            "--color-secondary": "#F43F5E",
            "--color-accent": "#FB7185",
            "--color-crimson": "#9F1239",
            "--color-background": "#180812",
            "--color-surface": "rgba(40, 14, 28, 0.72)",
            "--color-surface-elevated": "rgba(58, 20, 40, 0.85)",
            "--color-text-primary": "#FFF1F2",
            "--color-text-secondary": "#FDA4AF",
            "--color-border": "rgba(225, 29, 72, 0.25)",
            "--color-glow": "rgba(225, 29, 72, 0.4)",
            "--gradient-hero": "linear-gradient(135deg, rgba(40, 14, 28, 0.95) 0%, rgba(24, 8, 18, 0.98) 100%)",
            "--gradient-promo": "linear-gradient(90deg, #E11D48 0%, #F43F5E 50%, #FB7185 100%)",
            "--shadow-card": "0 8px 32px -4px rgba(225, 29, 72, 0.15)",
            "--radius-card": "1.25rem"
        },
        "campaign": {
            "badge": "\ud83d\udc98 Season of Affection",
            "headline": "Game Together, Win Together",
            "description": "Share the love with sweetheart duo packages, co-op enhancements, and romantic discounts",
            "ctaPrimary": {
                "label": "Browse Duo Deals",
                "action": "/products"
            },
            "ctaSecondary": {
                "label": "Gift a Friend",
                "action": "/products?category=bundle"
            },
            "motif": "heart",
            "secondarySlide": {
                "badge": "\ud83d\udc8c Love Notes",
                "headline": "Gift Codes & Instant Notifications",
                "description": "Receive private surprise gift codes directly in your Telegram chat throughout Valentine week.",
                "ctaPrimary": {
                    "label": "Join Romance Club",
                    "action": "https://t.me/pshstore"
                },
                "ctaSecondary": {
                    "label": "Profile Bind",
                    "action": "/profile"
                }
            }
        }
    },
    "blackfriday": {
        "id": "blackfriday",
        "name": "Black Friday",
        "title": "High-Impact Sale",
        "eventName": "Black Friday",
        "effect": "none",
        "schedule": {
            "start": "11-20",
            "end": "11-30"
        },
        "backgroundPattern": "radial-gradient(circle at 80% 20%, rgba(250, 204, 21, 0.14) 0%, transparent 45%), radial-gradient(circle at 20% 80%, rgba(225, 29, 72, 0.14) 0%, transparent 50%), radial-gradient(circle at 50% 50%, rgba(56, 189, 248, 0.06) 0%, transparent 60%)",
        "colors": {
            "--primary-50": "254 252 232",
            "--primary-100": "254 249 195",
            "--primary-200": "254 240 138",
            "--primary-300": "253 224 71",
            "--primary-400": "250 204 21",
            "--primary-500": "234 179 8",
            "--primary-600": "202 138 4",
            "--primary-700": "161 98 7",
            "--primary-800": "133 77 14",
            "--primary-900": "113 63 18",
            "--secondary-50": "255 241 242",
            "--secondary-100": "255 228 230",
            "--secondary-200": "254 205 211",
            "--secondary-300": "253 164 175",
            "--secondary-400": "251 113 133",
            "--secondary-500": "244 63 94",
            "--secondary-600": "225 29 72",
            "--secondary-700": "190 18 60",
            "--secondary-800": "159 18 57",
            "--secondary-900": "136 19 55",
            "--accent-50": "240 249 255",
            "--accent-100": "224 242 254",
            "--accent-200": "186 230 253",
            "--accent-300": "125 211 252",
            "--accent-400": "56 189 248",
            "--accent-500": "14 165 233",
            "--accent-600": "2 132 199",
            "--accent-700": "3 105 161",
            "--accent-800": "7 89 133",
            "--accent-900": "12 74 110"
        },
        "tokens": {
            "--color-primary": "#FACC15",
            "--color-secondary": "#E11D48",
            "--color-accent": "#38BDF8",
            "--color-crimson": "#DC2626",
            "--color-background": "#09090B",
            "--color-surface": "rgba(24, 24, 27, 0.75)",
            "--color-surface-elevated": "rgba(39, 39, 42, 0.88)",
            "--color-text-primary": "#FAFAFA",
            "--color-text-secondary": "#A1A1AA",
            "--color-border": "rgba(250, 204, 21, 0.3)",
            "--color-glow": "rgba(250, 204, 21, 0.45)",
            "--gradient-hero": "linear-gradient(135deg, rgba(24, 24, 27, 0.95) 0%, rgba(9, 9, 11, 0.98) 100%)",
            "--gradient-promo": "linear-gradient(90deg, #FACC15 0%, #EF4444 50%, #38BDF8 100%)",
            "--shadow-card": "0 8px 32px -4px rgba(250, 204, 21, 0.15)",
            "--radius-card": "1.25rem"
        },
        "campaign": {
            "badge": "\u26a1 Black Friday Cyber Week",
            "headline": "Maximum Power. Lowest Prices.",
            "description": "Unprecedented discounts on all premium game mods, VIP passes, and enterprise scripts",
            "ctaPrimary": {
                "label": "Shop Doorbusters",
                "action": "/products"
            },
            "ctaSecondary": {
                "label": "View Mega Bundles",
                "action": "/products?category=bundle"
            },
            "motif": "lightning",
            "secondarySlide": {
                "badge": "\ud83d\ude80 Flash Intel",
                "headline": "Lightning Deal Alerts on Telegram",
                "description": "Black Friday flash deals sell out fast. Connect your Telegram to grab flash drops before anyone else.",
                "ctaPrimary": {
                    "label": "Get Flash Alerts",
                    "action": "https://t.me/pshstore"
                },
                "ctaSecondary": {
                    "label": "Verify Account",
                    "action": "/profile"
                }
            }
        }
    },
    "diwali": {
        "id": "diwali",
        "name": "Diwali",
        "title": "Festival of Lights",
        "eventName": "Grand Diwali",
        "effect": "diwali",
        "schedule": {
            "start": "10-25",
            "end": "11-15"
        },
        "backgroundPattern": "radial-gradient(circle at 80% 12%, rgba(245, 158, 11, 0.18) 0%, transparent 45%), radial-gradient(circle at 20% 85%, rgba(220, 38, 38, 0.14) 0%, transparent 50%), radial-gradient(circle at 50% 0%, rgba(251, 191, 36, 0.10) 0%, rgba(120, 53, 15, 0.05) 65%, transparent 100%)",
        "colors": {
            "--primary-50": "255 251 235",
            "--primary-100": "254 243 199",
            "--primary-200": "253 230 138",
            "--primary-300": "252 211 77",
            "--primary-400": "251 191 36",
            "--primary-500": "245 158 11",
            "--primary-600": "217 119 6",
            "--primary-700": "180 83 9",
            "--primary-800": "146 64 14",
            "--primary-900": "120 53 15",
            "--secondary-50": "254 242 242",
            "--secondary-100": "254 226 226",
            "--secondary-200": "254 202 202",
            "--secondary-300": "252 165 165",
            "--secondary-400": "248 113 113",
            "--secondary-500": "239 68 68",
            "--secondary-600": "220 38 38",
            "--secondary-700": "185 28 28",
            "--secondary-800": "153 27 27",
            "--secondary-900": "127 29 29",
            "--accent-50": "250 245 255",
            "--accent-100": "243 232 255",
            "--accent-200": "233 213 255",
            "--accent-300": "216 180 254",
            "--accent-400": "192 132 252",
            "--accent-500": "168 85 247",
            "--accent-600": "147 51 234",
            "--accent-700": "126 34 206",
            "--accent-800": "107 33 168",
            "--accent-900": "88 28 135"
        },
        "tokens": {
            "--color-primary": "#F59E0B",
            "--color-secondary": "#DC2626",
            "--color-accent": "#8B5CF6",
            "--color-crimson": "#B91C1C",
            "--color-background": "#160B05",
            "--color-surface": "rgba(45, 20, 10, 0.72)",
            "--color-surface-elevated": "rgba(62, 28, 14, 0.85)",
            "--color-text-primary": "#FEF3C7",
            "--color-text-secondary": "#FCD34D",
            "--color-border": "rgba(245, 158, 11, 0.3)",
            "--color-glow": "rgba(245, 158, 11, 0.45)",
            "--gradient-hero": "linear-gradient(135deg, rgba(45, 20, 10, 0.95) 0%, rgba(22, 11, 5, 0.98) 100%)",
            "--gradient-promo": "linear-gradient(90deg, #F59E0B 0%, #DC2626 50%, #8B5CF6 100%)",
            "--shadow-card": "0 8px 32px -4px rgba(245, 158, 11, 0.2)",
            "--radius-card": "1.25rem"
        },
        "campaign": {
            "badge": "\ud83e\ude94 Shubh Deepawali 2026",
            "headline": "Illuminate Your Gaming Journey",
            "description": "Celebrate the triumph of mastery with sacred festive rewards, sparkling bundles, and golden credits",
            "ctaPrimary": {
                "label": "Claim Diwali Offers",
                "action": "/products"
            },
            "ctaSecondary": {
                "label": "Golden Bundles",
                "action": "/products?category=bundle"
            },
            "motif": "diya",
            "secondarySlide": {
                "badge": "\u2728 Shubh Aashirwad",
                "headline": "Festive Telegram Rewards & Instant Updates",
                "description": "Join the community for festive Diya coupon codes, gift drops, and round-the-clock support updates.",
                "ctaPrimary": {
                    "label": "Join Community",
                    "action": "https://t.me/pshstore"
                },
                "ctaSecondary": {
                    "label": "Connect Telegram",
                    "action": "/profile"
                }
            }
        }
    },
    "eid": {
        "id": "eid",
        "name": "Eid",
        "title": "Elegant Celebration",
        "eventName": "Eid Mubarak",
        "effect": "celebration",
        "schedule": {
            "start": "03-25",
            "end": "04-15"
        },
        "backgroundPattern": "radial-gradient(circle at 80% 15%, rgba(16, 185, 129, 0.16) 0%, transparent 45%), radial-gradient(circle at 20% 85%, rgba(245, 158, 11, 0.14) 0%, transparent 50%), radial-gradient(circle at 50% 50%, rgba(6, 182, 212, 0.08) 0%, transparent 60%)",
        "colors": {
            "--primary-50": "236 253 245",
            "--primary-100": "209 250 229",
            "--primary-200": "167 243 208",
            "--primary-300": "110 231 183",
            "--primary-400": "52 211 153",
            "--primary-500": "16 185 129",
            "--primary-600": "5 150 105",
            "--primary-700": "4 120 87",
            "--primary-800": "6 95 70",
            "--primary-900": "6 78 59",
            "--secondary-50": "255 251 235",
            "--secondary-100": "254 243 199",
            "--secondary-200": "253 230 138",
            "--secondary-300": "252 211 77",
            "--secondary-400": "251 191 36",
            "--secondary-500": "245 158 11",
            "--secondary-600": "217 119 6",
            "--secondary-700": "180 83 9",
            "--secondary-800": "146 64 14",
            "--secondary-900": "120 53 15",
            "--accent-50": "236 254 255",
            "--accent-100": "207 250 254",
            "--accent-200": "165 243 252",
            "--accent-300": "103 232 249",
            "--accent-400": "34 211 238",
            "--accent-500": "6 182 212",
            "--accent-600": "8 145 178",
            "--accent-700": "14 116 144",
            "--accent-800": "21 94 117",
            "--accent-900": "22 78 99"
        },
        "tokens": {
            "--color-primary": "#10B981",
            "--color-secondary": "#F59E0B",
            "--color-accent": "#06B6D4",
            "--color-crimson": "#E11D48",
            "--color-background": "#061811",
            "--color-surface": "rgba(12, 42, 30, 0.72)",
            "--color-surface-elevated": "rgba(18, 58, 42, 0.85)",
            "--color-text-primary": "#ECFDF5",
            "--color-text-secondary": "#A7F3D0",
            "--color-border": "rgba(16, 185, 129, 0.25)",
            "--color-glow": "rgba(16, 185, 129, 0.4)",
            "--gradient-hero": "linear-gradient(135deg, rgba(12, 42, 30, 0.95) 0%, rgba(6, 24, 17, 0.98) 100%)",
            "--gradient-promo": "linear-gradient(90deg, #10B981 0%, #F59E0B 50%, #06B6D4 100%)",
            "--shadow-card": "0 8px 32px -4px rgba(16, 185, 129, 0.15)",
            "--radius-card": "1.25rem"
        },
        "campaign": {
            "badge": "\ud83c\udf19 Eid Mubarak 2026",
            "headline": "Celebrate With Joy & Blessings",
            "description": "Special Eidi discounts on all gaming utilities, exclusive gift credits, and bundle packs",
            "ctaPrimary": {
                "label": "Explore Eidi Specials",
                "action": "/products"
            },
            "ctaSecondary": {
                "label": "Celebration Bundles",
                "action": "/products?category=bundle"
            },
            "motif": "crescent",
            "secondarySlide": {
                "badge": "\ud83c\udf81 Eidi Direct Drop",
                "headline": "Special Eidi Gifts in Telegram",
                "description": "Claim your holiday gift tokens and receive real-time updates directly via Telegram.",
                "ctaPrimary": {
                    "label": "Join Channel",
                    "action": "https://t.me/pshstore"
                },
                "ctaSecondary": {
                    "label": "Bind Profile",
                    "action": "/profile"
                }
            }
        }
    },
    "summersale": {
        "id": "summersale",
        "name": "Summer Sale",
        "title": "Tropical Energy",
        "eventName": "Summer Sale",
        "effect": "none",
        "schedule": {
            "start": "06-01",
            "end": "08-15"
        },
        "backgroundPattern": "radial-gradient(circle at 80% 20%, rgba(6, 182, 212, 0.16) 0%, transparent 45%), radial-gradient(circle at 20% 80%, rgba(249, 115, 22, 0.14) 0%, transparent 50%), radial-gradient(circle at 50% 50%, rgba(234, 179, 8, 0.08) 0%, transparent 60%)",
        "colors": {
            "--primary-50": "236 254 255",
            "--primary-100": "207 250 254",
            "--primary-200": "165 243 252",
            "--primary-300": "103 232 249",
            "--primary-400": "34 211 238",
            "--primary-500": "6 182 212",
            "--primary-600": "8 145 178",
            "--primary-700": "14 116 144",
            "--primary-800": "21 94 117",
            "--primary-900": "22 78 99",
            "--secondary-50": "255 247 237",
            "--secondary-100": "255 237 213",
            "--secondary-200": "254 215 170",
            "--secondary-300": "253 186 116",
            "--secondary-400": "251 146 60",
            "--secondary-500": "249 115 22",
            "--secondary-600": "234 88 12",
            "--secondary-700": "194 65 12",
            "--secondary-800": "154 52 18",
            "--secondary-900": "124 45 18",
            "--accent-50": "254 252 232",
            "--accent-100": "254 249 195",
            "--accent-200": "254 240 138",
            "--accent-300": "253 224 71",
            "--accent-400": "250 204 21",
            "--accent-500": "234 179 8",
            "--accent-600": "202 138 4",
            "--accent-700": "161 98 7",
            "--accent-800": "133 77 14",
            "--accent-900": "113 63 18"
        },
        "tokens": {
            "--color-primary": "#06B6D4",
            "--color-secondary": "#F97316",
            "--color-accent": "#EAB308",
            "--color-crimson": "#EF4444",
            "--color-background": "#071521",
            "--color-surface": "rgba(15, 39, 58, 0.72)",
            "--color-surface-elevated": "rgba(22, 54, 80, 0.85)",
            "--color-text-primary": "#ECFEFF",
            "--color-text-secondary": "#A5F3FC",
            "--color-border": "rgba(6, 182, 212, 0.25)",
            "--color-glow": "rgba(6, 182, 212, 0.4)",
            "--gradient-hero": "linear-gradient(135deg, rgba(15, 39, 58, 0.95) 0%, rgba(7, 21, 33, 0.98) 100%)",
            "--gradient-promo": "linear-gradient(90deg, #06B6D4 0%, #F97316 50%, #EAB308 100%)",
            "--shadow-card": "0 8px 32px -4px rgba(6, 182, 212, 0.15)",
            "--radius-card": "1.25rem"
        },
        "campaign": {
            "badge": "\u2600\ufe0f Tropical Summer Drop",
            "headline": "Sun-Drenched Summer Deals",
            "description": "Dive into refreshing discounts, hot seasonal releases, and unlimited play passes",
            "ctaPrimary": {
                "label": "Browse Summer Deals",
                "action": "/products"
            },
            "ctaSecondary": {
                "label": "Unlimited Passes",
                "action": "/products?category=subscription"
            },
            "motif": "sun",
            "secondarySlide": {
                "badge": "\ud83c\udf0a Wave Alerts",
                "headline": "Summer Drops & Instant Telegram Alerts",
                "description": "Get notified immediately when new game updates and hot summer tools go live.",
                "ctaPrimary": {
                    "label": "Join Community",
                    "action": "https://t.me/pshstore"
                },
                "ctaSecondary": {
                    "label": "Link Account",
                    "action": "/profile"
                }
            }
        }
    }
};

/**
 * Resolves the currently active theme based on:
 * 1. URL preview parameter (?theme=... or ?previewTheme=...)
 * 2. Settings configured activeTheme from database (or admin panel)
 * 3. Date-based automated schedule matching today's date
 * 4. Default fallback ('default')
 */
export const resolveActiveTheme = (settingsTheme, previewParam) => {
    let preview = previewParam;
    if (!preview && typeof window !== 'undefined' && window.location) {
        try {
            const searchParams = new URLSearchParams(window.location.search);
            preview = searchParams.get('theme') || searchParams.get('previewTheme');
        } catch {
            // Ignore if window.location is unavailable
        }
    }

    const param = (preview || '').toLowerCase().trim();
    if (param && themes[param]) {
        return param;
    }

    const explicit = (settingsTheme || '').toLowerCase().trim();
    if (explicit && themes[explicit] && explicit !== 'auto') {
        return explicit;
    }

    // Automated date scheduling
    const now = new Date();
    const currentMonth = now.getMonth() + 1; // 1-12
    const currentDay = now.getDate();
    const mmdd = String(currentMonth).padStart(2, '0') + '-' + String(currentDay).padStart(2, '0');

    for (const [key, theme] of Object.entries(themes)) {
        if (theme.schedule && theme.schedule.start && theme.schedule.end) {
            const { start, end } = theme.schedule;
            // Handle ranges that wrap across year-end (e.g., Dec 29 to Jan 10)
            if (start <= end) {
                if (mmdd >= start && mmdd <= end) return key;
            } else {
                if (mmdd >= start || mmdd <= end) return key;
            }
        }
    }

    return 'default';
};

export const DEFAULT_THEME = 'default';
export const THEME_KEYS = Object.keys(themes);

export const getThemeConfig = (themeKey) => {
    const key = (themeKey || '').toLowerCase();
    return themes[key] || themes.default;
};
